import { Button } from '@accure/ui';
import { Calendar, Check, Copy, FileText, Mail, RotateCw } from 'lucide-react';

import { CommunicationSync } from '@/api/fe-api/view/mutations/communication-sync';
import { Tooltip } from '@/components/ui/tooltip';

import { useEmail } from '../hooks/use-email';

export function CommunicationEmpty() {
  const communicationSync = CommunicationSync.useMutation();
  const [email, copyEmail, isCopied] = useEmail();

  function handleRefresh() {
    communicationSync.mutate({});
  }

  return (
    <div className="flex h-full flex-col items-center justify-center p-6 text-center">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8">
          <h2 className="mb-3 text-2xl font-semibold text-gray-900">
            Welcome to ACCURE Communications
          </h2>
          <p className="text-gray-600">
            Your central hub for all battery analytics communications. Track,
            manage, and collaborate efficiently.
          </p>
        </div>

        <div className="mb-12 grid grid-cols-3 gap-6">
          <div className="rounded-lg bg-gray-50/50 p-4">
            <Mail className="mx-auto mb-2 size-6 text-gray-600" />
            <h3 className="mb-1 font-medium text-gray-900">Emails</h3>
            <p className="text-sm text-gray-600">
              All your battery analytics correspondence in one place
            </p>
          </div>
          <div className="rounded-lg bg-gray-50/50 p-4">
            <Calendar className="mx-auto mb-2 size-6 text-gray-600" />
            <h3 className="mb-1 font-medium text-gray-900">Meetings</h3>
            <p className="text-sm text-gray-600">
              Track discussions and decisions about your battery systems
            </p>
          </div>
          <div className="rounded-lg bg-gray-50/50 p-4">
            <FileText className="mx-auto mb-2 size-6 text-gray-600" />
            <h3 className="mb-1 font-medium text-gray-900">Minutes</h3>
            <p className="text-sm text-gray-600">
              Keep detailed records of every important meeting
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-md rounded-lg border border-gray-100 bg-white p-6">
          <h4 className="mb-2 font-medium text-gray-800">Get Started</h4>
          <p className="mb-4 text-sm text-gray-600">
            Forward your email to the address below to start receiving
            communications:
          </p>
          <div className="flex items-center justify-between rounded-md border border-gray-100 bg-gray-50 p-2">
            <code className="font-mono text-sm text-gray-700">{email}</code>
            <Button
              variant="ghost"
              size="sm"
              className="size-7 p-0 text-gray-500"
              onClick={copyEmail}
              aria-label="Copy email address"
            >
              {isCopied ? (
                <Check className="size-4 text-green-500" />
              ) : (
                <Copy className="size-4" />
              )}
            </Button>
          </div>

          <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
            <span>Sent an email already?</span>
            <Tooltip content="Check for new communications">
              <Button
                onClick={handleRefresh}
                disabled={communicationSync.isLoading}
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-gray-600 hover:bg-gray-50 hover:text-primary-600"
              >
                {communicationSync.isLoading ? (
                  <RotateCw className="size-4 animate-spin" />
                ) : (
                  <>
                    <RotateCw className="mr-1 size-4" />
                    <span>Refresh</span>
                  </>
                )}
              </Button>
            </Tooltip>
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-md border-t border-gray-100 pt-4 text-xs text-gray-500">
          <p>
            By sending emails to ACCURE, you acknowledge and agree to our
            <a
              href="https://www.accure.net/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="mx-1 text-gray-600 hover:underline"
            >
              Data Processing Policy
            </a>
            and consent to the storage of your communication data.
          </p>
        </div>
      </div>
    </div>
  );
}
